import {
  IonButtons,
  IonCard,
  IonCol,
  IonDatetime,
  IonDatetimeButton,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonMenuButton,
  IonModal,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSearchbar,
  IonSkeletonText,
  IonText,
  IonTitle,
  IonToolbar,
  RefresherEventDetail,
  useIonAlert,
} from '@ionic/react';
import { arrowDownOutline, calendar } from 'ionicons/icons';
import moment from 'moment';
import { Fragment, useEffect, useRef, useState } from 'react';

import { isPlatform } from '@ionic/core/components';
import { unwrapResult } from '@reduxjs/toolkit';
import { useHistory } from 'react-router';
import { logTransaction } from '../../common/log';
import { getClockingId, getJobId, getStateDateAsString, getStateStringAsMoment } from '../../common/util';
import { ClockingDirection, ClockingType, IClockingHistory } from '../../models/dms/clocking.model';
import { IRepairOrder } from '../../models/dms/repairOrder.model';
import { IRepairOrderJob } from '../../models/dms/repairOrderJob.model';
import { UNKNOWN_TECH } from '../../models/dms/technician.model';
import { IThunkError } from '../../models/error.model';
import { LogType } from '../../models/log.model';
import { RouteNames } from '../../models/routes.model';
import { useClockOffLabourModal } from '../../providers/ClockOffLabourModalProvider';
import { useDataSync } from '../../providers/DataSyncProvider';
import { useToast } from '../../providers/ToastProvider';
import store from '../../store';
import { setIsInActionState } from '../../store/appSlice';
import { fetchClockingHistoryStoreAsync, setSelectedClockingHistoryState } from '../../store/clockingHistorySlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clockOnLabourClockingAsync, resumeLabourClockingAsync } from '../../store/technicianClockingSlice';
import { DashboardSelectedTab } from '../dashboardFooter/DashboardFooter';
import ProfileChip from '../profileChip/ProfileChip';
import { IonContentWrapper, IonHeaderWrapper } from './ClockingHistoryList.styles';
import ClockingHistoryCard from '../clockingHistoryCard/ClockingHistoryCard';

interface IComponentProps {}

const ClockingHistoryList: React.FC<IComponentProps> = () => {
  const [isAndroid, setIsAndroid] = useState(true);
  const [selectedDate, setSelectedDate] = useState(moment().toISOString());
  const {
    state: { requestQueueSyncTimer },
  } = useDataSync();
  const {
    actions: { errorToast },
  } = useToast();
  const {
    actions: { showClockOffLabourModal },
  } = useClockOffLabourModal();
  const datetimeModal = useRef<null | HTMLIonModalElement>(null);

  const [presentAlert] = useIonAlert();
  const history = useHistory();

  const { technician } = useAppSelector((state) => state.auth);
  const { selectedClockingHistory, selectedClockingHitoryLoading } = useAppSelector((state) => state.clockingHistory);

  const dispatch = useAppDispatch();

  useEffect(() => {
    setIsAndroid(!isPlatform('ios'));
    loadClockingHistory(moment().toISOString(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    loadClockingHistory(selectedDate);
    // Add a fake "loading" progress to make it look like the app is working ;)
    setTimeout(() => {
      event.detail.complete();
    }, 1000);
  };

  const handleChangeDate = (value: string) => {
    setSelectedDate(value);
    loadClockingHistory(value);
  };

  const loadClockingHistory = async (clockingDate: string) => {
    const techCode = technician?.code || UNKNOWN_TECH;
    dispatch(setSelectedClockingHistoryState({}));
    await dispatch(fetchClockingHistoryStoreAsync({ techCode, clockingDate: clockingDate }));
  };

  const handleClickClockingHistory = (clockingHistory: IClockingHistory) => {
    if (clockingHistory.type === ClockingType.Labour) {
      const jobData = {
        branchCode: clockingHistory.branchCode,
        repairOrderNumber: clockingHistory.repairOrderNumber,
        jobCode: clockingHistory.jobCode,
        jobType: clockingHistory.jobType,
      } as IRepairOrderJob;
      handleLabourClocking(jobData);
    }
  };

  const handleLabourClocking = async (job: IRepairOrderJob) => {
    // Attempt to pull the repair order info from the cache
    const cachedRo = store.getState().repairOrderSync.repairOrderSync?.data.find((ro) => ro.repairOrderNumber === job.repairOrderNumber);
    const repairOrder =
      cachedRo ||
      ({
        repairOrderNumber: job.repairOrderNumber,
        branch: job.branchCode,
      } as IRepairOrder);

    const { labourClocking } = store.getState().technicianClocking;

    if (labourClocking) {
      const jobTypeCodeRo = getClockingId(labourClocking);
      const matchingJob = getJobId(job) === jobTypeCodeRo;

      if (labourClocking.direction === ClockingDirection.On || labourClocking.direction === ClockingDirection.Break) {
        // Current labour is either on or on a break
        if (matchingJob) {
          // Trying to clock on existing or paused job
          if (labourClocking.direction === ClockingDirection.Break) {
            // Resume job
            presentResumeAlert(job);
          } else {
            // Clock off
            showClockOffLabourModal(job, repairOrder);
          }
        } else {
          if (labourClocking.direction === ClockingDirection.On) {
            // Clocking on a new job while on a job
            showClockOffLabourModal(job, repairOrder);
          } else {
            // Clocking on a job while another job is paused
            presentClockOnAlert(technician?.code || UNKNOWN_TECH, job, repairOrder, repairOrder.registrationNumber);
          }
        }
      } else {
        // Current labour is off
        presentClockOnAlert(technician?.code || UNKNOWN_TECH, job, repairOrder, repairOrder.registrationNumber);
      }
    } else {
      // Current labour is off
      presentClockOnAlert(technician?.code || UNKNOWN_TECH, job, repairOrder, repairOrder.registrationNumber);
    }
  };

  const presentClockOnAlert = async (technicianCode: string, jobData: IRepairOrderJob, repairOrder: IRepairOrder, registrationNumber: string) => {
    await presentAlert({
      header: 'Clock On?',
      subHeader: '',
      message: jobData.isAdhoc ? `Are you sure you want to clock on this adhoc job?` : `Are you sure you want to clock on job ${jobData.jobType}/${jobData.jobCode}?`,
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary-button',
        },
        {
          text: 'Yes',
          cssClass: 'secondary-button',
          handler: async () => {
            await clockOnLabour(technicianCode, jobData, repairOrder, registrationNumber);
            history.push({
              pathname: RouteNames.dashboard,
              search: `tab=${DashboardSelectedTab.ClockingSummary}`,
            });
          },
        },
      ],
      onDidDismiss: () => {
        dispatch(setIsInActionState(false));
      },
    });

    dispatch(setIsInActionState(true));
  };

  const presentResumeAlert = async (jobData: IRepairOrderJob) => {
    await presentAlert({
      header: 'Resume?',
      subHeader: '',
      message: jobData.isAdhoc ? `Are you sure you want to resume this adhoc job?` : `Are you sure you want to resume job ${jobData.jobType}/${jobData.jobCode}?`,
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          cssClass: 'secondary-button',
        },
        {
          text: 'Yes',
          cssClass: 'secondary-button',
          handler: async () => {
            await resumeLabour();
          },
        },
      ],
      onDidDismiss: () => {
        dispatch(setIsInActionState(false));
      },
    });

    dispatch(setIsInActionState(true));
  };

  const clockOnLabour = async (technicianCode: string, jobData: IRepairOrderJob, repairOrder: IRepairOrder, registrationNumber: string) => {
    const startDateTime = new Date();
    const { lastPosition } = store.getState().location;

    try {
      logTransaction(LogType.Info, `Clock on labour requested - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}`);
      const resultAction = await dispatch(
        clockOnLabourClockingAsync({
          techCode: technicianCode,
          startDateTime: getStateDateAsString(startDateTime),
          jobData: jobData,
          repairOrder: repairOrder,
          position: lastPosition,
          registrationNumber: registrationNumber,
        }),
      );
      unwrapResult(resultAction);
      requestQueueSyncTimer?.fire();
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to clock on labour - jobData: ${jobData.repairOrderNumber}/${jobData.jobCode}. ${err}`);
      errorToast(err);
    }
  };

  const resumeLabour = async () => {
    const startDateTime = new Date();
    const { lastPosition } = store.getState().location;

    try {
      logTransaction(LogType.Info, `Resume labour requested`);
      const resultAction = await dispatch(
        resumeLabourClockingAsync({
          startDateTime: getStateDateAsString(startDateTime),
          position: lastPosition,
        }),
      );
      unwrapResult(resultAction);
      requestQueueSyncTimer?.fire();
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to resume labour. ${err}`);
      errorToast(err);
    }
  };

  return (
    <>
      <IonHeaderWrapper color="lightest">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonMenuButton color="secondary" />
          </IonButtons>
          <IonTitle>History</IonTitle>
          <ProfileChip slot="end" />
        </IonToolbar>
        <IonToolbar color="primary">
          {/* Load css for searchbar component */}
          <IonSearchbar style={{ display: 'none' }} />
          {/* android/web control */}
          {isAndroid && (
            <div className="sc-ion-searchbar-md-h">
              <div className="searchbar-input-container sc-ion-searchbar-md">
                <div className="searchbar-input sc-ion-searchbar-md">
                  <IonDatetimeButton className="date-time-button" datetime="datetime" color="dark"></IonDatetimeButton>
                  <IonModal ref={datetimeModal} keepContentsMounted>
                    <IonDatetime id="datetime" presentation="date" showDefaultButtons color="calendar" onIonChange={(val) => handleChangeDate(val.detail.value as string)} />
                  </IonModal>
                </div>
                <IonIcon icon={calendar} className="searchbar-search-icon sc-ion-searchbar-md" />
              </div>
            </div>
          )}
          {/* ios/web control */}
          {!isAndroid && (
            <div className="sc-ion-searchbar-ios-h sc-ion-searchbar-ios-s ios searchbar-left-aligned searchbar-should-show-clear" role="search">
              <div className="searchbar-input-container sc-ion-searchbar-ios">
                <div className="searchbar-input sc-ion-searchbar-ios">
                  <IonDatetimeButton className="date-time-button" datetime="datetime" color="dark"></IonDatetimeButton>
                  <IonModal ref={datetimeModal} keepContentsMounted>
                    <IonDatetime id="datetime" presentation="date" showDefaultButtons color="calendar" onIonChange={(val) => handleChangeDate(val.detail.value as string)} />
                  </IonModal>
                </div>
                <IonIcon icon={calendar} className="searchbar-search-icon sc-ion-searchbar-ios ios" />
              </div>
            </div>
          )}
        </IonToolbar>
      </IonHeaderWrapper>
      <IonContentWrapper color="lightest">
        <IonRefresher slot="fixed" onIonRefresh={(event) => doRefresh(event)}>
          <IonRefresherContent pullingIcon={arrowDownOutline} pullingText="" refreshingSpinner={'lines'} refreshingText="" />
        </IonRefresher>
        <IonGrid className="ion-no-padding">
          {!(selectedClockingHitoryLoading || Object.keys(selectedClockingHistory).length >= 1) && (
            <p className="ion-text-center">
              <IonText color="primary">No history found for the selected date</IonText>
            </p>
          )}

          {Object.entries(selectedClockingHistory).map(([clockingDateTime, entries], index1) => (
            <Fragment key={`group-${index1}`}>
              <IonItemDivider>{getStateStringAsMoment(clockingDateTime).format('HH:mm')}</IonItemDivider>
              {entries?.map((clockingHistory, index2) => (
                <IonRow key={`clocking-${index1}-${index2}`}>
                  <IonCol>
                    <ClockingHistoryCard clockingHistory={clockingHistory} handleClick={() => handleClickClockingHistory(clockingHistory)} />
                  </IonCol>
                </IonRow>
              ))}
            </Fragment>
          ))}

          {selectedClockingHitoryLoading && (
            <>
              <IonItemDivider>
                <IonSkeletonText animated />
              </IonItemDivider>
              <IonRow>
                <IonCol>
                  <IonCard>
                    <IonItem lines="none">
                      <IonSkeletonText animated />
                    </IonItem>
                  </IonCard>
                </IonCol>
              </IonRow>
            </>
          )}
        </IonGrid>
      </IonContentWrapper>
    </>
  );
};

export default ClockingHistoryList;
